import { useCallback, useEffect, useState } from "react";
import { RTFOPumpdownChart } from "./RTFOPumpdownChart";
import { RTFOPumpdownFalloffChart } from "./RTFOPumpdownFalloffChart";
import { Accordion, AccordionDetails, AccordionSummary, Button, Divider, FormControl, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Cancel, ExpandMore, Restore, Save, VerticalAlignBottom, VerticalAlignCenter } from "@mui/icons-material";
import { useSocket } from "../../context/useSocket";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import { setStageData } from "../../reducers/rtfoSlice";
import { RTFOPumpdownDataTable } from "./RTFOPumpdownDataTable";
import { RTFOPumpdownDeltaChart } from "./RTFOPumpdownDeltaChart";
import PowerRegression from "ml-regression-power";
import SimpleLinearRegression from "ml-regression-simple-linear";
import RTFOStageSummary from "./RTFOStageSummary";
import { useTheme } from "@mui/styles";
// import RTFOContinuousPressureChart from "./RTFOContinuousPressureChart";

export const RTFOPumpdown = ({ currentRTFO, stageData, pumpdownData, isLoadingPumpdownData, sdtType, gridView, darkMode }) => {
  
  const { socket } = useSocket();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const [pumpdownFalloffTimestamps, setPumpdownFalloffTimestamps] = useState(() => {
    if (stageData?.pumpdownParams?.pumpdownFalloffTimestamps) {
      return stageData?.pumpdownParams?.pumpdownFalloffTimestamps;
    } else {
      const firstRateIndex = pumpdownData?.findIndex(d => d.rate > 1);
      const falloffStartTimestamp = pumpdownData?.find((d,i) => i > firstRateIndex && d.rate < 0.1)?.timestamp;
      const falloffEndTimestamp = pumpdownData?.find((d,i) => d.timestamp > falloffStartTimestamp && d.rate > 0.1)?.timestamp;
      return { startTimestamp: falloffStartTimestamp, endTimestamp: falloffEndTimestamp };
    };
  });
  const [pumpdownRegression, setPumpdownRegression] = useState(null);
  const [postPerfRegression, setPostPerfRegression] = useState(null);
  const [pressureTestTimestamps, setPressureTestTimestamps] = useState(stageData?.pumpdownParams?.pressureTestTimestamps || {});
  const [postPlugFalloffTimestamps, setPostPlugFalloffTimestamps] = useState(stageData?.pumpdownParams?.postPlugFalloffTimestamps || {});
  const [postPerfFalloffTimestamps, setPostPerfFalloffTimestamps] = useState(() => {
    if (stageData?.pumpdownParams?.postPerfFalloffTimestamps) {
      return stageData?.pumpdownParams?.postPerfFalloffTimestamps;
    } else {
      const falloffStartIndex = pumpdownData?.findLastIndex(d => d.rate > 0.1);
      const falloffStartTimestamp = pumpdownData?.[falloffStartIndex]?.timestamp;
      const falloffStartDepth = pumpdownData?.[falloffStartIndex]?.depth;
      const falloffEndTimestamp = pumpdownData?.findLast((d, i) => i > falloffStartIndex && (Math.abs(falloffStartDepth - d.depth)) < 2)?.timestamp;
      return { startTimestamp: falloffStartTimestamp, endTimestamp: falloffEndTimestamp };
    };
  });
  const [customTimestamps, setCustomTimestamps] = useState(stageData?.pumpdownParams?.customTimestamps || {});
  const [editMode, setEditMode] = useState(false);
  const [initialRegression, setInitialRegression] = useState(null);
  const [initialRegressionStartTimestamp, setInitialRegressionStartTimestamp] = useState(null);
  const [pumpdownRegressionStartTimestamp, setPumpdownRegressionStartTimestamp] = useState(stageData?.pumpdownParams?.pumpdownRegressionStartTimestamp);
  const [postPerfRegressionStartTimestamp, setPostPerfRegressionStartTimestamp] = useState(stageData?.pumpdownParams?.postPerfRegressionStartTimestamp);
  const [pumpdownISIPLinearRegression, setPumpdownISIPLinearRegression] = useState(null);
  const [postPlugISIPLinearRegression, setPostPlugISIPLinearRegression] = useState(null);
  const [postPerfISIPLinearRegression, setPostPerfISIPLinearRegression] = useState(null);
  const [customISIPLinearRegression, setCustomISIPLinearRegression] = useState(null);
  const [pumpdownISIP, setPumpdownISIP] = useState(stageData?.pumpdownParams?.pumpdownISIP || 0);
  const [postPerfISIP, setPostPerfISIP] = useState(stageData?.pumpdownParams?.postPerfISIP || 0);
  const [postPlugISIP, setPostPlugISIP] = useState(stageData?.pumpdownParams?.postPlugISIP || 0);
  const [customISIP, setCustomISIP] = useState(stageData?.pumpdownParams?.customISIP || 0);
  const [chartType, setChartType] = useState('Delta ISIP');

  const calculatePumpdown = useCallback((pumpdownData) => {
    try {
      
      console.log('Calculating pumpdown...');

      if (!pumpdownData?.length > 0) return;

      const XInitialTimestamp = [];
      const XInitialTime = [];
      const YInitial = [];
      // Isolate an initial flat pressure interval and generate power law fit
      const initialStartTimestamp = pumpdownData.find((d,i) => d.depth > 5 && pumpdownData?.[i + 300]?.depth > 100 && d.pressure > 20 && Math.abs(d?.pressure - pumpdownData?.[i + 60]?.pressure) < 1)?.timestamp;
      const initialEndTimestamp = pumpdownData.find((d,i) => d.depth > 50 && d.rate > 0.1 && pumpdownData[i+60]?.rate > 0.5)?.timestamp - 1000;
      pumpdownData?.forEach(d => {
        if (d.timestamp > initialStartTimestamp && d.timestamp < initialEndTimestamp && d.pressure > 0) {
          const time = (d.timestamp - initialStartTimestamp) / 60000;
          XInitialTimestamp.push(d.timestamp);
          XInitialTime.push(time);
          YInitial.push(d.pressure);
        };
      });
      const initialFit = new PowerRegression(XInitialTime, YInitial);
      setInitialRegression(initialFit);
      setInitialRegressionStartTimestamp(initialStartTimestamp);
      
      // Calculate pumpdown ISIP power law fit
      const XPumpdownISIPTimestamp = [];
      const XPumpdownISIPTime = [];
      const YPumpdownISIP = [];
      // Determine the 0-rate starting point
      let pumpdownISIPStartTimestamp = pumpdownRegressionStartTimestamp || stageData?.pumpdownParams?.pumpdownRegressionStartTimestamp;
      if (!pumpdownISIPStartTimestamp) {
        pumpdownISIPStartTimestamp = pumpdownData?.findLast((d,i) => d.timestamp < pumpdownFalloffTimestamps?.startTimestamp && d.rate > 0.1 && pumpdownData[i-1]?.rate > d.rate && pumpdownData[i-30]?.rate > 0.5)?.timestamp;
      };

      let postPerfISIPStartTimestamp = postPerfRegressionStartTimestamp || stageData?.pumpdownParams?.postPerfRegressionStartTimestamp;
      if (!postPerfISIPStartTimestamp) {
        postPerfISIPStartTimestamp = pumpdownData?.findLast((d,i) => d.timestamp > pumpdownISIPStartTimestamp && d.timestamp < postPerfFalloffTimestamps?.startTimestamp && d.rate > 0.1 && pumpdownData[i-1]?.rate > d.rate && pumpdownData[i-10]?.rate > 0.1)?.timestamp || postPerfFalloffTimestamps?.startTimestamp;
      };

      // Add the pumpdown ISIP interval for fitting using the defined timestamps
      pumpdownData.forEach(d => {
        if (d.timestamp >= pumpdownFalloffTimestamps?.startTimestamp && d.timestamp <= pumpdownFalloffTimestamps?.endTimestamp && d.pressure > 0) {
          XPumpdownISIPTimestamp.push(d.timestamp);
          XPumpdownISIPTime.push((d.timestamp - pumpdownISIPStartTimestamp) / 60000);
          YPumpdownISIP.push(d.pressure);
        };
      });
      // Calculate linear pumpdown ISIP fit
      const regression = new SimpleLinearRegression(XPumpdownISIPTime?.filter(d => (d * 60000 + pumpdownISIPStartTimestamp) >= pumpdownFalloffTimestamps?.startTimestamp), YPumpdownISIP?.filter((_,i) => (XPumpdownISIPTime[i] * 60000 + pumpdownISIPStartTimestamp) >= pumpdownFalloffTimestamps?.startTimestamp));
      if (regression.intercept > 0) {
        setPumpdownISIP(regression.intercept);
        setPumpdownISIPLinearRegression(regression);
      } else {
        setPumpdownISIP(null);
        setPumpdownISIPLinearRegression(null);
      };
      // Add the end of the initial fit interval
      // const tailLengthPumpdown = Math.max(Math.floor(XPumpdownISIPTime.length * 0.25), 4);
      const XInitialFit = [];
      const YInitialFit = [];
      pumpdownData.forEach((d,i) => {
        if (d.timestamp > initialStartTimestamp) {
          XInitialFit.push(d.timestamp);
          YInitialFit.push(initialFit.predict((d.timestamp - initialStartTimestamp) / 60000));
        };
      });
      // XInitialFit.forEach((d,i) => {
      //   const time = (d - pumpdownISIPStartTimestamp) / 60000;
      //   if (i > XInitialFit.length - tailLengthPumpdown && time > 0) {
      //     XPumpdownISIPTimestamp.push(d);
      //     XPumpdownISIPTime.push(time);
      //     YPumpdownISIP.push(YInitialFit[i]);
      //   };
      // });
      const pumpdownISIPRegression = new PowerRegression(XPumpdownISIPTime, YPumpdownISIP);
      setPumpdownRegression(pumpdownISIPRegression);
      setPumpdownRegressionStartTimestamp(pumpdownISIPStartTimestamp);
      

      // Calculate post perf linear isip regression
      let XPostPerfISIPTimestamp = [];
      let XPostPerfISIPTime = [];
      let YPostPerfISIP = [];
      // Add the post perf ISIP interval for fitting using the defined timestamps
      pumpdownData.forEach(d => {
        if (d.timestamp >= postPerfFalloffTimestamps?.startTimestamp && d.timestamp <= postPerfFalloffTimestamps?.endTimestamp && d.pressure > 0) {
          XPostPerfISIPTimestamp.push(d.timestamp);
          XPostPerfISIPTime.push((d.timestamp - postPerfISIPStartTimestamp) / 60000);
          YPostPerfISIP.push(d.pressure);
        };
      });
      // Calculate linear post perf ISIP fit
      const postPerfRegression = new SimpleLinearRegression(XPostPerfISIPTime?.slice(parseInt(XPostPerfISIPTime?.length * 0.25), ), YPostPerfISIP?.slice(parseInt(YPostPerfISIP?.length * 0.25), ));
      if (postPerfRegression?.intercept > 0) {
        setPostPerfISIPLinearRegression(postPerfRegression);
        setPostPerfISIP(postPerfRegression.intercept);
        setPostPerfRegressionStartTimestamp(postPerfISIPStartTimestamp);
      } else {
        setPostPerfISIPLinearRegression(null);
        setPostPerfISIP(0);
        setPostPerfRegressionStartTimestamp(null);
      };
      // Add the end of the initial fit interval
      // XPostPerfISIPTimestamp = XPostPerfISIPTimestamp.slice(0, parseInt(XPostPerfISIPTimestamp?.length / 2));
      // XPostPerfISIPTime = XPostPerfISIPTime.slice(0, parseInt(XPostPerfISIPTime?.length / 2));
      // YPostPerfISIP = YPostPerfISIP.slice(0, parseInt(YPostPerfISIP?.length / 2));
      // const tailLengthPostPerf = Math.max(Math.floor(XPostPerfISIPTime.length * 0.2), 4);
      // XInitialFit.forEach((d,i) => {
      //   const time = (d - postPerfISIPStartTimestamp) / 60000;
      //   if (i > XInitialFit.length - tailLengthPostPerf && time > 0) {
      //     XPostPerfISIPTimestamp.push(d);
      //     XPostPerfISIPTime.push(time);
      //     YPostPerfISIP.push(YInitialFit[i]);
      //   };
      // });
      const postPerfISIPRegression = new PowerRegression(XPostPerfISIPTime, YPostPerfISIP);

      setPostPerfRegression(postPerfISIPRegression);
      setPostPerfRegressionStartTimestamp(postPerfISIPStartTimestamp);


      // Calculate post plug linear isip regresssion
      const XPostPlugISIPTimestamp = [];
      const XPostPlugISIPTime = [];
      const YPostPlugISIP = [];
      // Add the post plug ISIP interval for fitting using the defined timestamps
      pumpdownData.forEach(d => {
        if (d.timestamp > postPlugFalloffTimestamps?.startTimestamp && d.timestamp < postPlugFalloffTimestamps?.endTimestamp && d.pressure > 0) {
          XPostPlugISIPTimestamp.push(d.timestamp);
          XPostPlugISIPTime.push((d.timestamp - postPlugFalloffTimestamps?.startTimestamp) / 60000);
          YPostPlugISIP.push(d.pressure);
        };
      });
      // Calculate linear post plug ISIP fit
      const postPlugRegression = new SimpleLinearRegression(XPostPlugISIPTime?.slice(parseInt(XPostPlugISIPTime?.length * 0.5), ), YPostPlugISIP?.slice(parseInt(YPostPlugISIP?.length * 0.5), ));
      if (postPlugRegression?.intercept > 0) {
        setPostPlugISIPLinearRegression(postPlugRegression);
        setPostPlugISIP(postPlugRegression?.intercept);
      } else {
        setPostPlugISIPLinearRegression(null);
        setPostPlugISIP(0);
      };
      
      // Calculate custom linear isip regresssion
      const XCustomISIPTimestamp = [];
      const XCustomISIPTime = [];
      const YCustomISIP = [];
      // Add the post perf ISIP interval for fitting using the defined timestamps
      pumpdownData.forEach(d => {
        if (d.timestamp > customTimestamps?.startTimestamp && d.timestamp < customTimestamps?.endTimestamp && d.pressure > 0) {
          XCustomISIPTimestamp.push(d.timestamp);
          XCustomISIPTime.push((d.timestamp - customTimestamps?.startTimestamp) / 60000);
          YCustomISIP.push(d.pressure);
        };
      });
      // Calculate linear post perf ISIP fit
      const customRegression = new SimpleLinearRegression(XCustomISIPTime?.slice(parseInt(XCustomISIPTime?.length * 0.5), ), YCustomISIP?.slice(parseInt(YCustomISIP?.length * 0.5), ));
      if (customRegression?.intercept > 0) {
        setCustomISIPLinearRegression(customRegression);
        setCustomISIP(customRegression?.intercept);
      } else {
        setCustomISIPLinearRegression(null);
        setCustomISIP(0);
      };

    } catch (error) {
      console.log(error);
      return;
    };
  }, [customTimestamps?.endTimestamp, customTimestamps?.startTimestamp, postPerfFalloffTimestamps?.endTimestamp, postPerfFalloffTimestamps?.startTimestamp, postPerfRegressionStartTimestamp, postPlugFalloffTimestamps?.endTimestamp, postPlugFalloffTimestamps?.startTimestamp, pumpdownFalloffTimestamps?.endTimestamp, pumpdownFalloffTimestamps?.startTimestamp, pumpdownRegressionStartTimestamp, stageData?.pumpdownParams?.postPerfRegressionStartTimestamp, stageData?.pumpdownParams?.pumpdownRegressionStartTimestamp]);

  function recalculatePumpdownTimestamps(isReset) {
    let pumpdownFalloffTimestamps = {};
    let pressureTestTimestamps = {};

    // DETERMINE PUMPDOWN INTERVAL
    if (stageData?.pumpdownParams?.pumpdownFalloffTimestamps?.startTimestamp > 0 && !isReset) {
      setPumpdownFalloffTimestamps(stageData?.pumpdownParams?.pumpdownFalloffTimestamps);
      pumpdownFalloffTimestamps = stageData?.pumpdownParams?.pumpdownFalloffTimestamps;
    } else {
      const firstRateIndex = pumpdownData?.findIndex(d => d.rate > 1 && d.depth > 100) || 0;
      const pumpdownFalloffStartTimestamp = pumpdownData?.find((d,i) => i > firstRateIndex && d.rate < 0.1)?.timestamp;
      const pumpdownFalloffEndTimestamp = pumpdownData?.find((d,i) => d.timestamp > pumpdownFalloffStartTimestamp && d.rate > 0.1)?.timestamp;
      pumpdownFalloffTimestamps = { startTimestamp: pumpdownFalloffStartTimestamp, endTimestamp: pumpdownFalloffEndTimestamp };
      setPumpdownFalloffTimestamps(pumpdownFalloffTimestamps);
    };
    
    // DETERMINE POST PERF INTERVAL
    if (stageData?.pumpdownParams?.postPerfFalloffTimestamps?.startTimestamp > 0 && !isReset) {
      setPostPerfFalloffTimestamps(stageData?.pumpdownParams?.postPerfFalloffTimestamps);
    } else {
      const postPerfFalloffStartIndex = pumpdownData?.findLastIndex(d => d.rate > 0.1);
      const postPerfFalloffStartTimestamp = pumpdownData?.[postPerfFalloffStartIndex]?.timestamp;
      const postPerfFalloffStartDepth = pumpdownData?.[postPerfFalloffStartIndex]?.depth;
      const postPerfFalloffEndTimestamp = pumpdownData?.find((d, i) => i > postPerfFalloffStartIndex && d.depth > 0 && (Math.abs(postPerfFalloffStartDepth - d.depth)) > 0.5)?.timestamp - 1000;
      setPostPerfFalloffTimestamps({ startTimestamp: postPerfFalloffStartTimestamp, endTimestamp: postPerfFalloffEndTimestamp });
    };

    // DETERMINE PRESSURE TEST INTERVAL
    if (stageData?.pumpdownParams?.pressureTestTimestamps && !isReset) {
      setPressureTestTimestamps(stageData?.pumpdownParams?.pressureTestTimestamps || {});
      pressureTestTimestamps = stageData?.pumpdownParams?.pressureTestTimestamps;
    } else {
      const pressureTestStartIndex = pumpdownData?.findIndex((d,i) => d.timestamp > pumpdownFalloffTimestamps?.endTimestamp && d.rate < 0.1 && pumpdownData[i-10]?.rate > 0.1) + 5;
      const pressureTestStartTimestamp = pumpdownData?.[pressureTestStartIndex]?.timestamp;
      const pressureTestStartDepth = pumpdownData?.[pressureTestStartIndex]?.depth;
      const pressureTestEndTimestamp = pumpdownData?.findLast((d, i) => i > pressureTestStartIndex && d.depth > 0 && (Math.abs(pressureTestStartDepth - d.depth)) < 2 && d.rate < 0.1)?.timestamp - 1000;
      pressureTestTimestamps = { startTimestamp: pressureTestStartTimestamp, endTimestamp: pressureTestEndTimestamp };
      setPressureTestTimestamps(pressureTestTimestamps);
    };

    // DETERMINE POST PLUG INTERVAL
    if (stageData?.pumpdownParams?.postPlugFalloffTimestamps?.startTimestamp > 0 && !isReset) {
      setPostPlugFalloffTimestamps(stageData?.pumpdownParams?.postPlugFalloffTimestamps);
    } else if (pressureTestTimestamps?.endTimestamp > 0) {
      const postPlugFalloffStartTimestamp = pressureTestTimestamps?.endTimestamp;
      const postPlugFalloffEndTimestamp = pumpdownData?.find(d => d.timestamp > postPlugFalloffStartTimestamp && d.rate > 0.1)?.timestamp;
      setPostPlugFalloffTimestamps({ startTimestamp: postPlugFalloffStartTimestamp, endTimestamp: postPlugFalloffEndTimestamp });
    } else {
      setPostPlugFalloffTimestamps({}); 
    };

    // DETERMINE CUSTOM INTERVAL
    if (stageData?.pumpdownParams?.customTimestamps?.startTimestamp > 0 && !isReset) {
      setCustomTimestamps(stageData?.pumpdownParams?.customTimestamps);
    } else {
      setCustomTimestamps(null);
    };

    setEditMode(null);

    if (isReset) handleSaveChanges();

  };

  // when pumpdown data changes, update the timestamps
  useEffect(() => {

    if (editMode) return;

    recalculatePumpdownTimestamps();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pumpdownData]);

  // when pumpdown data changes, update the timestamps
  useEffect(() => {

    setPumpdownRegressionStartTimestamp(stageData?.pumpdownParams?.pumpdownRegressionStartTimestamp);
    setPostPerfRegressionStartTimestamp(stageData?.pumpdownParams?.postPerfRegressionStartTimestamp);

    // Calculate pumpdown params
    calculatePumpdown(pumpdownData);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatePumpdown, pumpdownData]);
  

  return (
    sdtType !== 'stageSummary' ?
    <div style={{display: 'flex', flexDirection: 'column'}}>
      
      {/* <RTFOContinuousPressureChart currentRTFO={currentRTFO} darkMode={darkMode} theme={theme} /> */}


      <div style={{position: 'relative', width: '100%', display: 'flex', flexDirection: gridView ? 'row' : 'column'}}>
        <div style={{width: gridView ? '50%' : '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', position: 'absolute', top: 5, left: 10, paddingRight: 20, zIndex: 421}}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
            
            <Button variant="contained" color={darkMode ? "inherit" : "primary"} size="small" startIcon={<Restore />} style={{height: 24, marginTop: 15}} onClick={() => recalculatePumpdownTimestamps(true)}>Reset</Button>

            <Divider flexItem orientation="vertical" style={{ margin: 5, marginTop: 15, height: 24 }} />

            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <Typography variant="subtitle2" color="textSecondary" style={{fontWeight: 'bold', fontSize: 10}}>
                PUMPDOWN
              </Typography>
              <ToggleButtonGroup
                value={editMode}
                exclusive
                onChange={(e, newType) => setEditMode(newType)}
                size="small"
                style={{height: 24, zIndex: 420}}
              >
                <ToggleButton value="pumpdownRegression" size="small" title="Edit Pumpdown Rate Zero" color={(darkMode ? "" : "primary")}>
                  <VerticalAlignCenter style={{transform: 'rotate(-90deg)', fontSize: '16px'}} />
                </ToggleButton>
                <ToggleButton value="pumpdownStart" size="small" title="Edit Pumpdown Start" color={(darkMode ? "" : "primary")}>
                  <VerticalAlignBottom style={{transform: 'rotate(90deg)', fontSize: '16px'}} />
                </ToggleButton>
                <ToggleButton value="pumpdownEnd" size="small" title="Edit Pumpdown End" color={(darkMode ? "" : "primary")}>
                  <VerticalAlignBottom style={{transform: 'rotate(-90deg)', fontSize: '16px'}} />
                </ToggleButton>
              </ToggleButtonGroup>

            </div>

            <Divider flexItem orientation="vertical" style={{ margin: 5, marginTop: 15, height: 24 }} />

            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <Typography variant="subtitle2" color="textSecondary" style={{fontWeight: 'bold', fontSize: 10}}>
                PRESS. TEST
              </Typography>
              <ToggleButtonGroup
                value={editMode}
                exclusive
                onChange={(e, newType) => setEditMode(newType)}
                size="small"
                style={{height: 24, zIndex: 420}}
              >
                <ToggleButton value="pressureTestStart" size="small" title="Edit Pressure Test Start" color={(darkMode ? "" : "primary")}>
                  <VerticalAlignBottom style={{transform: 'rotate(90deg)', fontSize: '16px'}} />
                </ToggleButton>
                <ToggleButton value="pressureTestEnd" size="small" title="Edit Pressure Test End" color={(darkMode ? "" : "primary")}>
                  <VerticalAlignBottom style={{transform: 'rotate(-90deg)', fontSize: '16px'}} />
                </ToggleButton>
              </ToggleButtonGroup>
            </div>

            <Divider flexItem orientation="vertical" style={{ margin: 5, marginTop: 15, height: 24 }} />

            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <Typography variant="subtitle2" color="textSecondary" style={{fontWeight: 'bold', fontSize: 10}}>
                POST PLUG
              </Typography>
              <ToggleButtonGroup
                value={editMode}
                exclusive
                onChange={(e, newType) => setEditMode(newType)}
                size="small"
                style={{height: 24, zIndex: 420}}
              >
                <ToggleButton value="postPlugStart" size="small" title="Edit Post Plug Start" color={(darkMode ? "" : "primary")}>
                  <VerticalAlignBottom style={{transform: 'rotate(90deg)', fontSize: '16px'}} />
                </ToggleButton>
                <ToggleButton value="postPlugEnd" size="small" title="Edit Post Plug End" color={(darkMode ? "" : "primary")}>
                  <VerticalAlignBottom style={{transform: 'rotate(-90deg)', fontSize: '16px'}} />
                </ToggleButton>
              </ToggleButtonGroup>
            </div>

            <Divider flexItem orientation="vertical" style={{ margin: 5, marginTop: 15, height: 24 }} />

            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <Typography variant="subtitle2" color="textSecondary" style={{fontWeight: 'bold', fontSize: 10}}>
                POST PERF
              </Typography>
              <ToggleButtonGroup
                value={editMode}
                exclusive
                onChange={(e, newType) => setEditMode(newType)}
                size="small"
                style={{height: 24, zIndex: 420}}
              >
                <ToggleButton value="postPerfRegression" size="small" title="Edit Post Perf Rate Zero" color={(darkMode ? "" : "primary")}>
                  <VerticalAlignCenter style={{transform: 'rotate(-90deg)', fontSize: '16px'}} />
                </ToggleButton>
                <ToggleButton value="postPerfStart" size="small" title="Edit Post Perf Start" color={(darkMode ? "" : "primary")}>
                  <VerticalAlignBottom style={{transform: 'rotate(90deg)', fontSize: '16px'}} />
                </ToggleButton>
                <ToggleButton value="postPerfEnd" size="small" title="Edit Post Perf End" color={(darkMode ? "" : "primary")}>
                  <VerticalAlignBottom style={{transform: 'rotate(-90deg)', fontSize: '16px'}} />
                </ToggleButton>
              </ToggleButtonGroup>
            </div>

            <Divider flexItem orientation="vertical" style={{ margin: 5, marginTop: 15, height: 24 }} />

            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
              <Typography variant="subtitle2" color="textSecondary" style={{fontWeight: 'bold', fontSize: 10}}>
                CUSTOM
              </Typography>
              <ToggleButtonGroup
                value={editMode}
                exclusive
                onChange={(e, newType) => setEditMode(newType)}
                size="small"
                style={{height: 24, zIndex: 420}}
              >
                <ToggleButton value="customStart" size="small" title="Edit Custom Start" color={(darkMode ? "" : "primary")}>
                  <VerticalAlignBottom style={{transform: 'rotate(90deg)', fontSize: '16px'}} />
                </ToggleButton>
                <ToggleButton value="customEnd" size="small" title="Edit Custom End" color={(darkMode ? "" : "primary")}>
                  <VerticalAlignBottom style={{transform: 'rotate(-90deg)', fontSize: '16px'}} />
                </ToggleButton>
              </ToggleButtonGroup>
            </div>

          </div>

          <ToggleButtonGroup
            value={''}
            size="small"
            style={{height: 24, display: !editMode && 'none'}}
            onChange={() => {}}
          >
            <ToggleButton value={'1'} size="small" title="Cancel Changes" color={(darkMode ? "" : "primary")} onClick={handleCancelChanges} >
              <Cancel style={{fontSize: '16px'}} />
            </ToggleButton>
            <ToggleButton value={'2'} size="small" title="Save Changes" color={(darkMode ? "" : "primary")} onClick={handleSaveChanges}>
              <Save style={{fontSize: '16px'}} />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        <div style={{width: gridView ? '50%' : '100%', height: gridView ? 580 : 900, marginTop: 0, paddingTop: 40, outline: editMode && '5px solid red', zIndex: 420, position: 'relative'}}>
          <div 
            style={{
              zIndex: 69, 
              position: 'absolute', 
              left: '50%', 
              bottom: 0, 
              transform: 'translateX(-50%)', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
            }}
          >
            {
              editMode === 'pumpdownStart' ? <Typography variant="body2" color="red">Click to move pumpdown start time</Typography> : 
              editMode === 'pumpdownEnd' ? <Typography variant="body2" color="red">Click to move pumpdown end time</Typography> : 
              editMode === 'pressureTestStart' ? <Typography variant="body2" color="red">Click to move pressure test start time</Typography> : 
              editMode === 'pressureTestEnd' ? <Typography variant="body2" color="red">Click to move pressure test end time</Typography> : 
              editMode === 'postPlugStart' ? <Typography variant="body2" color="red">Click to move post plug start time</Typography> : 
              editMode === 'postPlugEnd' ? <Typography variant="body2" color="red">Click to move post plug end time</Typography> :
              editMode === 'postPerfStart' ? <Typography variant="body2" color="red">Click to move post perf start time</Typography> : 
              editMode === 'postPerfEnd' ? <Typography variant="body2" color="red">Click to move post perf end time</Typography> : 
              editMode === 'customStart' ? <Typography variant="body2" color="red">Click to move custom start time</Typography> : 
              editMode === 'customEnd' ? <Typography variant="body2" color="red">Click to move custom end time</Typography> : 
              editMode === 'pumpdownRegression' ? <Typography variant="body2" color="red">Click to move pumpdown rate zero time</Typography> : 
              editMode === 'postPerfRegression' ? <Typography variant="body2" color="red">Click to move post perf rate zero time</Typography> : 
              ''
            }
          </div>
          
          <RTFOPumpdownChart
            gridView={gridView} 
            showSpikeLines={true} 
            pumpdownData={pumpdownData}
            pumpdownFalloffTimestamps={pumpdownFalloffTimestamps}
            setPumpdownFalloffTimestamps={setPumpdownFalloffTimestamps}
            pressureTestTimestamps={pressureTestTimestamps}
            setPressureTestTimestamps={setPressureTestTimestamps}
            postPlugFalloffTimestamps={postPlugFalloffTimestamps}
            setPostPlugFalloffTimestamps={setPostPlugFalloffTimestamps}
            postPerfFalloffTimestamps={postPerfFalloffTimestamps}
            setPostPerfFalloffTimestamps={setPostPerfFalloffTimestamps}
            customTimestamps={customTimestamps}
            setCustomTimestamps={setCustomTimestamps}
            isLoadingPumpdownData={isLoadingPumpdownData}
            editMode={editMode}
            pumpdownRegression={pumpdownRegression}
            setPumpdownRegression={setPumpdownRegression}
            pumpdownRegressionStartTimestamp={pumpdownRegressionStartTimestamp}
            setPumpdownRegressionStartTimestamp={setPumpdownRegressionStartTimestamp}
            postPerfRegression={postPerfRegression}
            postPerfRegressionStartTimestamp={postPerfRegressionStartTimestamp}
            setPostPerfRegressionStartTimestamp={setPostPerfRegressionStartTimestamp}
            initialRegression={initialRegression}
            initialRegressionStartTimestamp={initialRegressionStartTimestamp}
          />
        </div>

        <div style={{width: gridView ? '50%' : '100%', height: gridView ? 580 : 900, marginTop: 0, paddingTop: 40}}>
          <RTFOPumpdownFalloffChart
            gridView={gridView} 
            showSpikeLines={true} 
            pumpdownData={pumpdownData}
            pumpdownFalloffTimestamps={pumpdownFalloffTimestamps}
            postPerfFalloffTimestamps={postPerfFalloffTimestamps}
            pumpdownISIP={pumpdownISIP}
            postPerfISIP={postPerfISIP}
            isLoadingPumpdownData={isLoadingPumpdownData}
            pumpdownRegression={pumpdownRegression}
            pumpdownRegressionStartTimestamp={pumpdownRegressionStartTimestamp}
            pumpdownISIPLinearRegression={pumpdownISIPLinearRegression}
            postPerfRegression={postPerfRegression}
            postPerfRegressionStartTimestamp={postPerfRegressionStartTimestamp}
            postPlugISIPLinearRegression={postPlugISIPLinearRegression}
            postPerfISIPLinearRegression={postPerfISIPLinearRegression}
            customISIPLinearRegression={customISIPLinearRegression}
          />
        </div>
      </div>

      <Accordion defaultExpanded disableGutters elevation={0} style={{backgroundColor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]}}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">
            Pumpdown Results
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <div style={{display: 'flex', flexDirection: 'row'}}>
            <div style={{height: 540, width: '50%', position: 'relative'}}>
              <div style={{display: 'flex', alignItems: 'center', position: 'absolute', top: -30, right: 10}}>
                <Typography style={{marginRight: 10}} fontSize='12px'>Chart type: </Typography>
                <FormControl>
                  <Select 
                    value={chartType}
                    onChange={(e) => {
                      setChartType(e.target.value);
                    }}
                    size="small"
                    variant="outlined"
                    style={{width: 125, height: 28, zIndex: 69, fontSize: '14px'}}
                  >
                    {['Delta ISIP', 'Pumpdown ISIP', 'Post Perf ISIP'].map(type => {
                      return (
                        <MenuItem value={type} key={type}>{type}</MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </div>
              <RTFOPumpdownDeltaChart currentRTFO={currentRTFO} gridView={true} chartType={chartType} />
            </div>
            <div style={{ height: 700, width: '50%' }}>
              <RTFOPumpdownDataTable currentRTFO={currentRTFO} pumpdownParams={stageData?.pumpdownParams} darkMode={darkMode} />
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

    </div> :
    <RTFOStageSummary
      currentRTFO={currentRTFO}
      gridView={false} 
      showSpikeLines={true} 
      pumpdownData={pumpdownData}
      pumpdownFalloffTimestamps={pumpdownFalloffTimestamps}
      setPumpdownFalloffTimestamps={setPumpdownFalloffTimestamps}
      pressureTestTimestamps={pressureTestTimestamps}
      setPressureTestTimestamps={setPressureTestTimestamps}
      postPlugFalloffTimestamps={postPlugFalloffTimestamps}
      setPostPlugFalloffTimestamps={setPostPlugFalloffTimestamps}
      postPerfFalloffTimestamps={postPerfFalloffTimestamps}
      setPostPerfFalloffTimestamps={setPostPerfFalloffTimestamps}
      customTimestamps={customTimestamps}
      setCustomTimestamps={setCustomTimestamps}
      isLoadingPumpdownData={isLoadingPumpdownData}
      editMode={editMode}
      pumpdownRegression={pumpdownRegression}
      setPumpdownRegression={setPumpdownRegression}
      pumpdownRegressionStartTimestamp={pumpdownRegressionStartTimestamp}
      postPerfRegression={postPerfRegression}
      postPerfRegressionStartTimestamp={postPerfRegressionStartTimestamp}
      initialRegression={initialRegression}
      initialRegressionStartTimestamp={initialRegressionStartTimestamp}
      darkMode={darkMode}
    />
  );

  function handleCancelChanges() {
    setEditMode(false);

    if (stageData?.pumpdownParams?.pumpdownRegressionStartTimestamp > 0) {
      setPumpdownRegressionStartTimestamp(stageData?.pumpdownParams?.pumpdownRegressionStartTimestamp);
    } else {
      setPumpdownRegressionStartTimestamp(null);
    };

    if (stageData?.pumpdownParams?.postPerfRegressionStartTimestamp > 0) {
      setPostPerfRegressionStartTimestamp(stageData?.pumpdownParams?.postPerfRegressionStartTimestamp);
    } else {
      setPostPerfRegressionStartTimestamp(null);
    };

    if (stageData?.pumpdownParams?.pumpdownFalloffTimestamps) {
      setPumpdownFalloffTimestamps(stageData?.pumpdownParams?.pumpdownFalloffTimestamps);
    } else {
      const firstRateIndex = pumpdownData?.findIndex(d => d.rate > 1);
      const pumpdownFalloffStartTimestamp = pumpdownData?.find((d,i) => i > firstRateIndex && d.rate < 0.1)?.timestamp;
      const pumpdownFalloffEndTimestamp = pumpdownData?.find((d,i) => d.timestamp > pumpdownFalloffStartTimestamp && d.rate > 0.1)?.timestamp;
      setPumpdownFalloffTimestamps({ startTimestamp: pumpdownFalloffStartTimestamp, endTimestamp: pumpdownFalloffEndTimestamp });
    };

    if (stageData?.pumpdownParams?.postPerfFalloffTimestamps) {
      setPostPerfFalloffTimestamps(stageData?.pumpdownParams?.postPerfFalloffTimestamps);
    } else {
      const postPerfFalloffStartIndex = pumpdownData?.findLastIndex(d => d.rate > 0.1);
      const postPerfFalloffStartTimestamp = pumpdownData?.[postPerfFalloffStartIndex]?.timestamp;
      const postPerfFalloffStartDepth = pumpdownData?.[postPerfFalloffStartIndex]?.depth;
      const postPerfFalloffEndTimestamp = pumpdownData?.findLast((d, i) => i > postPerfFalloffStartIndex && (Math.abs(postPerfFalloffStartDepth - d.depth)) < 100)?.timestamp;
      setPostPerfFalloffTimestamps({ startTimestamp: postPerfFalloffStartTimestamp, endTimestamp: postPerfFalloffEndTimestamp });
    };

    setPressureTestTimestamps(stageData?.pumpdownParams?.pressureTestTimestamps || {});
  };

  function handleSaveChanges() {
    try {
      setEditMode(false);
  
      // calculate pumpdown params
      const pressureTestStart = pumpdownData?.find(d => d.timestamp === pressureTestTimestamps?.startTimestamp); 
      const pressureTestEnd = pumpdownData?.find(d => d.timestamp === pressureTestTimestamps?.endTimestamp); 
  
      const pressureTestIncrease = pressureTestEnd?.pressure - pressureTestStart?.pressure;
      const pressureTestDuration = (postPerfFalloffTimestamps?.endTimestamp - postPerfFalloffTimestamps?.startTimestamp) / 1000 / 60;
      const pressureTestOverISIPStart = pressureTestStart?.pressure - pumpdownISIP;
      const pressureTestOverISIPEnd = pressureTestEnd?.pressure - pumpdownISIP;
  
  
      const pumpdownParams = {
        pumpdownTimestamps: [pumpdownData?.[0]?.timestamp, pumpdownData?.[pumpdownData?.length - 1]?.timestamp],
        pumpdownRegressionStartTimestamp,
        pumpdownFalloffTimestamps,
        postPerfFalloffTimestamps,
        postPerfRegressionStartTimestamp,
        pressureTestTimestamps,
        postPlugFalloffTimestamps,
        customTimestamps,
        pumpdownISIP,
        postPerfISIP,
        postPlugISIP,
        customISIP,
        pressureTestIncrease,
        pressureTestDuration,
        pressureTestOverISIPStart,
        pressureTestOverISIPEnd,
        stageIsolation: stageData?.pumpdownParams?.stageIsolation || null,
        prevZeroProppantFracfeed: stageData?.pumpdownParams?.prevZeroProppantFracfeed || null,
        currentPreSandFracfeed: stageData?.pumpdownParams?.currentPreSandFracfeed || null
      };
  
      dispatch(setStageData({stageData: {...stageData, pumpdownParams}, seismicData: null, initialLoad: false, highResolutionData: true, liveMonitoring: false, incrementalData: false}));
  
      socket?.send(JSON.stringify({
        messageType: 'savePumpdownParams',
        stageName: stageData?.name,
        pumpdownParams
      }));
      enqueueSnackbar(`Stage ${stageData?.name} saved successfully`, { variant: 'success' });
      
    } catch (error) {
      console.log(error);
      return;
    };
  };
};